import {
  Component,
  ChangeDetectionStrategy,
  input,
  viewChild,
  DestroyRef,
  inject,
} from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import {
  faChevronDown,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { NavItem } from "./nav-config";
import { HasCapabilityDirective } from "../shared/directives/has-capability.directive";

@Component({
  selector: "app-nav-dropdown",
  imports: [
    RouterModule,
    TranslateModule,
    FontAwesomeModule,
    MatMenuModule,
    HasCapabilityDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "inline-block" },
  template: `
    <button
      #trigger="matMenuTrigger"
      [matMenuTriggerFor]="menu"
      class="group relative flex items-center gap-1 px-3 py-2 text-mty-body hover:text-mty-primary aria-expanded:text-mty-primary transition-colors cursor-pointer"
      (mouseenter)="onTriggerEnter()"
      (mouseleave)="onTriggerLeave()"
      (menuOpened)="onMenuOpened()"
      (menuClosed)="onMenuClosed()"
    >
      <span>{{ item().labelKey | translate }}</span>
      <fa-icon
        [icon]="faChevronDown"
        class="text-[10px] transition-transform duration-200 group-aria-expanded:rotate-180"
      ></fa-icon>
      <span
        class="absolute bottom-0 left-1/2 w-0 h-[2px] bg-mty-primary transition-[width,left] duration-300 ease-out group-hover:w-[60%] group-hover:left-[20%] group-aria-expanded:w-[60%] group-aria-expanded:left-[20%]"
      ></span>
    </button>
    <mat-menu
      #menu="matMenu"
      [hasBackdrop]="false"
      panelClass="nav-dropdown-panel"
    >
      @for (child of item().children; track child.labelKey) {
        <ng-container *appHasCapability="child.capability">
          @if (child.externalUrl) {
            <a
              mat-menu-item
              [href]="child.externalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="nav-dropdown-item"
            >
              <span>{{ child.labelKey | translate }}</span>
              <fa-icon
                [icon]="faExternal"
                class="nav-dropdown-external-icon"
              ></fa-icon>
            </a>
          } @else {
            <a
              mat-menu-item
              [routerLink]="resolveRoute()(child)"
              [queryParams]="child.queryParams"
              routerLinkActive="nav-dropdown-item-active"
              class="nav-dropdown-item"
            >
              <span>{{ child.labelKey | translate }}</span>
            </a>
          }
        </ng-container>
      }
    </mat-menu>
  `,
})
export class NavDropdownComponent {
  // ── Static coordinator: only one dropdown open at a time ──
  private static activeInstance: NavDropdownComponent | null = null;

  private destroyRef = inject(DestroyRef);

  readonly item = input.required<NavItem>();
  readonly resolveRoute = input.required<(item: NavItem) => string>();
  readonly trigger = viewChild<MatMenuTrigger>("trigger");
  faChevronDown = faChevronDown;
  faExternal = faArrowUpRightFromSquare;

  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private panelListeners: (() => void)[] = [];

  onTriggerEnter(): void {
    this.cancelClose();

    // If another dropdown is open, close it immediately (no delay)
    const prev = NavDropdownComponent.activeInstance;
    if (prev && prev !== this) {
      prev.closeImmediately();
    }

    const t = this.trigger();
    if (t && !t.menuOpen) {
      NavDropdownComponent.activeInstance = this;
      t.openMenu();
    }
  }

  onTriggerLeave(): void {
    this.scheduleClose();
  }

  onMenuOpened(): void {
    NavDropdownComponent.activeInstance = this;

    // Attach hover listeners to the CDK overlay panel
    setTimeout(() => {
      const t = this.trigger();
      if (t) {
        const panelId = (t.menu as any)?.panelId;
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          const enter = () => this.cancelClose();
          const leave = () => this.scheduleClose();
          panel.addEventListener("mouseenter", enter);
          panel.addEventListener("mouseleave", leave);
          this.panelListeners = [
            () => panel.removeEventListener("mouseenter", enter),
            () => panel.removeEventListener("mouseleave", leave),
          ];
        }
      }
    });
  }

  onMenuClosed(): void {
    this.cleanupPanelListeners();
    if (NavDropdownComponent.activeInstance === this) {
      NavDropdownComponent.activeInstance = null;
    }
  }

  /** Close without delay — used when switching between dropdowns */
  closeImmediately(): void {
    this.cancelClose();
    const t = this.trigger();
    if (t?.menuOpen) {
      t.closeMenu();
    }
  }

  private scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      const t = this.trigger();
      if (t?.menuOpen) {
        t.closeMenu();
      }
    }, 120);
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private cleanupPanelListeners(): void {
    this.panelListeners.forEach((fn) => fn());
    this.panelListeners = [];
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.cancelClose();
      this.cleanupPanelListeners();
      if (NavDropdownComponent.activeInstance === this) {
        NavDropdownComponent.activeInstance = null;
      }
    });
  }
}
